import * as cmd from "./Commands";
import { Response } from "./generated/wss_pb";
import { useState } from "react";

// See service_routines.md for where all of this comes from

interface Routine {
    id: number;
    name: string;
    // True if the module implements "request routine results" (KWP 0x33) for this routine.
    // The rest only acknowledge the start request.
    hasResults: boolean;
}

const routines: Routine[] = [
    { id: 0x20, name: 'Brake pressure sensors', hasResults: false },
    { id: 0x21, name: 'Steering angle sensor', hasResults: true },
    { id: 0x22, name: 'Lateral acceleration sensor', hasResults: false },
    { id: 0x24, name: 'Longitudinal acceleration sensor', hasResults: false },
    { id: 0x28, name: 'Inlet valves', hasResults: true },
    { id: 0x29, name: 'Master cylinder isolation valve', hasResults: true },
];

const nrcMeanings = new Map<number, string>([
    [0x12, 'routine not supported, or not started'],
    [0x21, 'busy, a previous routine is still running'],
    [0x22, 'conditions not correct: vehicle must be stationary with ignition on and no blocking faults'],
    [0x23, 'routine not complete'],
    [0x33, 'access denied, or vehicle is moving'],
]);

const NrcBusy = 0x21;
const NrcRoutineNotComplete = 0x23;

// Module drops out of the diag session (aborting the routine) if it hears nothing for ~2 seconds
const pollIntervalMs = 500;
const resultTimeoutMs = 20000;

const toHex = (data: Uint8Array): string =>
    Array.from(data).map(b => b.toString(16).toUpperCase().padStart(2, '0')).join(' ');

const sleep = (ms: number) => new Promise(f => setTimeout(f, ms));

type KwpResult =
    | { kind: 'positive', data: Uint8Array }
    | { kind: 'negative', nrc: number }
    | { kind: 'error', message: string };

// Routine commands return the module's raw KWP response: figure out what it said
const parseKwpResponse = (response: Response | undefined, serviceId: number): KwpResult => {
    if (response?.hasNak()) {
        const nakMsg = new TextDecoder().decode(response.getNak()!.getErrormessage_asU8()).replace(/\0+$/, '');
        return { kind: 'error', message: nakMsg || 'unknown error' };
    }

    if (!response?.hasReaddata()) {
        return { kind: 'error', message: 'no response' };
    }

    const data = response.getReaddata()!.getData_asU8();

    if (data.length >= 3 && data[0] === 0x7F && data[1] === serviceId) {
        return { kind: 'negative', nrc: data[2] };
    }

    if (data.length >= 3 && data[0] === 0x7F) {
        // Firmware hands back the response to an earlier step (session start) if the module refused that instead
        return { kind: 'error', message: `module refused request ${toHex(data.subarray(1, 2))} with code ${toHex(data.subarray(2, 3))}` };
    }

    if (data.length >= 1 && data[0] === serviceId + 0x40) {
        return { kind: 'positive', data };
    }

    return { kind: 'error', message: `unexpected response ${toHex(data)}` };
};

const describeFailure = (result: KwpResult): string => {
    if (result.kind === 'negative') {
        const code = result.nrc.toString(16).toUpperCase().padStart(2, '0');
        const meaning = nrcMeanings.get(result.nrc);
        return `module refused with code ${code}` + (meaning ? ` (${meaning})` : '');
    } else if (result.kind === 'error') {
        return result.message;
    }

    return '';
};

type Props = Readonly<{
    port: SerialPort;
    onDisconnect: (reason: string) => void;
}>;

export const ServiceRoutines = ({ port, onDisconnect }: Props): JSX.Element => {
    const [resultText, setResultText] = useState<string>();
    const [isRunning, setIsRunning] = useState(false);

    const runRoutine = async (routine: Routine) => {
        let output = `${routine.name} calibration: starting...\n`;
        setResultText(output);

        const log = (line: string) => {
            output += line + '\n';
            setResultText(output);
        };

        const startResult = parseKwpResponse(await cmd.startRoutine(port, onDisconnect, routine.id), 0x31);
        if (startResult.kind !== 'positive') {
            log(`Failed to start: ${describeFailure(startResult)}`);
            return;
        }

        log('Module accepted the calibration request.');

        // Anything after the routine ID is data from the module
        const startData = startResult.data.subarray(2);
        if (startData.length > 0) {
            log(`Response data: ${toHex(startData)}`);
        }

        if (routine.hasResults) {
            log('Waiting for result...');

            const deadline = Date.now() + resultTimeoutMs;
            while (true) {
                await sleep(pollIntervalMs);

                const pollResult = parseKwpResponse(await cmd.routineResults(port, onDisconnect, routine.id), 0x33);

                if (pollResult.kind === 'positive') {
                    log(`Finished, result data: ${toHex(pollResult.data.subarray(2))}`);
                    break;
                }

                const stillRunning = pollResult.kind === 'negative' && (pollResult.nrc === NrcRoutineNotComplete || pollResult.nrc === NrcBusy);
                if (!stillRunning) {
                    log(`Failed to read result: ${describeFailure(pollResult)}`);
                    break;
                }

                if (Date.now() > deadline) {
                    log('Timed out waiting for the calibration to finish.');
                    break;
                }
            }
        } else {
            // A scan tool that's known to work reads ident straight after the start response and
            // sends nothing else, letting the session lapse by itself. Do the same.
            await cmd.readIdentData(port, onDisconnect);

            log('Done.');
            log('\tThis calibration does not report a result:');
            log('\tRead DTCs to check for calibration faults.');
        }
    };

    const onRunRoutine = async (routine: Routine) => {
        setIsRunning(true);
        try {
            await runRoutine(routine);
        } finally {
            setIsRunning(false);
        }
    };

    return (
        <>
            <h2>Service Routines</h2>
            <div className='warning'>Calibrations require the vehicle to be stationary with the ignition on, and no faults stored that block the hydraulics or sensors.</div>
            <p />
            <h3>Calibration</h3>
            {routines.map(r => (
                <button type='button' key={r.id} disabled={isRunning} onClick={() => onRunRoutine(r)}>{r.name}</button>
            ))}
            {resultText &&
                <>
                    <p />
                    <pre>{resultText}</pre>
                </>}
        </>
    );
};
